import { DeleteOutlined, MailOutlined } from '@ant-design/icons'
import { useCookieState, useInViewport } from 'ahooks'
import { Button, List, Row, Typography } from 'antd'
import classnames from 'classnames'
import * as React from 'react'
import { useIntl } from 'react-intl'

import { StopPropagation } from '@/components/common/buttons/StopPropagation'
import { IconText } from '@/components/common/IconText'
import { RelativeTime } from '@/components/common/RelativeTime'
import { MailOpenOutlined } from '@/components/ui/icons'
import { HttpMethods } from '@/config/urls'
import { accountUrls } from '@/config/urls/account'
import { useThemeContext } from '@/context/ThemeContext'
import { useMatchMutate } from '@/hooks/useMatchMutate'
import { parseApiDateTimeTimezone } from '@/utils/datetime'
import { COOKIES, httpFetch } from '@/utils/http'
import { buildI18nFunction } from '@/utils/messages'

import { AccountNotification } from '@/types/index.d'

import styles from './Notifications.module.css'

type NotificationsListItemProps = {
  onNotificationOpenClick: (item: AccountNotification) => void
  onNotificationDismissClick: (item: AccountNotification) => void
  onNotificationRead: (item: AccountNotification) => void
  item: AccountNotification
  workspaceId: string
}

export function NotificationsListItem({
  onNotificationOpenClick,
  onNotificationDismissClick,
  onNotificationRead,
  item,
  workspaceId,
}: NotificationsListItemProps): JSX.Element {
  const { isLightMode, isDarkMode } = useThemeContext()
  const className = classnames({
    [styles.new]: !item.read,
    [styles.read]: item.read && !item.dismissed,
    [styles.dismissed]: item.dismissed,
    [styles['new-light']]: isLightMode && !item.read,
    [styles['read-light']]: isLightMode && item.read && !item.dismissed,
    [styles['dismissed-light']]: isLightMode && item.dismissed,
    [styles['read-dark']]: isDarkMode && item.read && !item.dismissed,
  })
  const matchMutate = useMatchMutate()
  const t = buildI18nFunction(useIntl().formatMessage)
  const ref = React.useRef(null)
  const [token] = useCookieState(COOKIES.XSRF_TOKEN)
  const [inViewport] = useInViewport(ref)

  React.useEffect(() => {
    if (inViewport && !item.read) {
      setTimeout(
        () =>
          httpFetch(
            accountUrls.getWorkspaceNotificationRead({
              workspaceId,
              notificationId: item.id,
            }),
            {
              method: HttpMethods.PATCH,
              xsrfToken: token,
            }
          ).then((response) => {
            if (response.ok) {
              onNotificationRead(item)
              matchMutate(/notifications/)
            }
          }),
        5000
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewport])

  let actions = [
    <StopPropagation key="open">
      <Button
        type="primary"
        size="small"
        onClick={() => onNotificationOpenClick(item)}
      >
        {t('SubMenu.notifications.open.button')}
      </Button>
    </StopPropagation>,
  ]
  if (!item.dismissed) {
    actions = [
      <StopPropagation key="dismiss">
        <Button
          type="link"
          size="small"
          onClick={() => onNotificationDismissClick(item)}
        >
          {t('SubMenu.notifications.dismiss.button')}
        </Button>
      </StopPropagation>,
      ...actions,
    ]
  }
  return (
    <List.Item onClick={() => onNotificationOpenClick(item)} actions={actions}>
      <List.Item.Meta
        data-cy={`Notifications.${item.id}`}
        description={
          <Typography.Paragraph
            className={className}
            ellipsis={{
              expandable: true,
              rows: 2,
              symbol: false,
            }}
            ref={ref}
            type="secondary"
          >
            {item?.description}
          </Typography.Paragraph>
        }
        key={item?.id}
        title={
          <Row justify={'space-between'} className={className}>
            <IconText icon={getIcon(item)} text={item?.title} />
            <Typography.Text className={styles.datetime} type="secondary">
              <RelativeTime
                timestamp={parseApiDateTimeTimezone(
                  item.creation_date
                ).getTime()}
              />
            </Typography.Text>
          </Row>
        }
      />
    </List.Item>
  )
}

function getIcon(item: AccountNotification) {
  if (item.dismissed) {
    return DeleteOutlined
  }
  if (item.read) {
    return MailOpenOutlined
  }
  return MailOutlined
}
